import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';

// Three-dot menu for one table row. The list opens in a portal with fixed positioning so the table can never clip it.
// items: [{ section }] headings or [{ label, Icon, onClick, tone }] entries (tone: danger | success | warn | primary)
const MENU_WIDTH = 230;

const RowActions = ({ items, label = 'Row actions' }) => {
    const [pos, setPos] = useState(null);
    const button = useRef(null);

    useEffect(() => {
        if (!pos) return undefined;
        const close = () => setPos(null);
        const onKey = (event) => { if (event.key === 'Escape') close(); };
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        document.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
            document.removeEventListener('keydown', onKey);
        };
    }, [pos]);

    const toggle = () => {
        if (pos) { setPos(null); return; }
        const box = button.current.getBoundingClientRect();
        const estimated = items.length * 40 + 16;
        const top = box.bottom + 6 + estimated > window.innerHeight ? Math.max(8, box.top - estimated - 6) : box.bottom + 6;
        setPos({ top, left: Math.max(8, Math.min(box.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8)) });
    };

    return (
        <>
            <button ref={button} type="button" className="users-kebab" onClick={toggle} aria-label={label} aria-haspopup="menu" aria-expanded={!!pos} title={label}>
                <MoreVertical size={18} />
            </button>
            {pos && createPortal(
                <>
                    <div className="users-menu-backdrop" onClick={() => setPos(null)} />
                    <div className="users-menu" role="menu" style={{ top: pos.top, left: pos.left, width: MENU_WIDTH }}>
                        {items.map((item, index) => (item.section ? (
                            <div key={`s-${index}`} className="users-menu__section">{item.section}</div>
                        ) : (
                            <button key={item.label} type="button" role="menuitem" className={`users-menu__item ${item.tone || ''} ${item.disabled ? 'is-disabled' : ''}`} aria-disabled={item.disabled || undefined} title={item.disabled ? item.hint : undefined}
                                onClick={() => { if (item.disabled) return; setPos(null); item.onClick(); }}>
                                {item.Icon && <item.Icon size={16} />}<span>{item.label}{item.disabled && item.hint && <small className="users-menu__hint">{item.hint}</small>}</span>
                            </button>
                        )))}
                    </div>
                </>,
                document.body,
            )}
        </>
    );
};

export default RowActions;
